// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation), pauses on hover/focus, and supports a
// sticky pause via a `--toggle` command from a button (WCAG 2.2.2). One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #hovered = false;
  #focused = false;
  #userPaused = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal });

    this.addEventListener("mouseenter", () => { this.#hovered = true; this.#sync(); }, { signal });
    this.addEventListener("mouseleave", () => { this.#hovered = false; this.#sync(); }, { signal });
    this.addEventListener("focusin", () => { this.#focused = true; this.#sync(); }, { signal });
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) { this.#focused = false; this.#sync(); }
    }, { signal });
    this.addEventListener("command", (event) => {
      if (event.command !== "--toggle") return;
      this.#userPaused = !this.#userPaused;
      event.source?.setAttribute("aria-pressed", String(this.#userPaused));
      this.#sync();
    }, { signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#animation?.cancel();
  }

  #sync() {
    if (!this.#animation) return;
    if (this.#hovered || this.#focused || this.#userPaused) this.#animation.pause();
    else this.#animation.play();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless; clones are hidden from AT.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((n) => {
        const clone = n.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    const distance = track.scrollWidth / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
    this.#sync();
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
